/**
 * @file: useDashboardInspectionFlow.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useInspectionTemplates } from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useMostAppropriateInspectionTemplate } from '@euler/functions/useMostAppropriateInspectionTemplate';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { sentry } from '@euler/lib/integration/sentry';
import { InspectionSiteInfo } from '@euler/model';
import {
  AbnormalLevel,
  InspectionTemplateSceneType,
  OptionValueType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { isNotNull } from '@euler/utils';
import { uniq } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useDashboardInspectionTemplate = () => {
  const templates = useInspectionTemplates({
    scene: InspectionTemplateSceneType.Dashboard,
  });
  return useMostAppropriateInspectionTemplate(templates);
};

export const useDashboardInspectionFlow = () => {
  const user = useCurrentUser();
  const storeConfiguration = useStoreConfiguration();
  const isWatermarkRequired =
    storeConfiguration.app?.orderFlow?.fields?.dashboardImage?.watermark ??
    false;

  const {
    taskNo,
    detail,
    taskManager,
    inventoryQuery: { getSiteById },
  } = useTaskContext();
  const { taskService } = useServiceFactory();

  const [sites, setSites] = useState<InspectionSiteInfo[]>();
  const [error, setError] = useState<Error>();

  const dashboardTemplate = useDashboardInspectionTemplate();

  const dashboardInspectionFlow = useMemo(
    () =>
      dashboardTemplate == null
        ? undefined
        : detail.preInspection.flows.find(
            x => x.template.originTemplateId === dashboardTemplate.id,
          ) ??
          detail.preInspection.flows.find(
            x => x.template.sceneType === InspectionTemplateSceneType.Dashboard,
          ),
    [dashboardTemplate, detail.preInspection.flows],
  );

  useEffect(() => {
    if (!dashboardTemplate) return;

    if (!dashboardInspectionFlow) {
      taskManager.preInspectionManager
        .addInspectionFlow({
          templateId: dashboardTemplate.id,
          assignToMemberId: user!.user.memberId,
        })
        .catch(err => {
          sentry.captureException(err);
          setError(err);
        });
    } else {
      const categories = dashboardInspectionFlow.template.conf.categories;
      const selectedSiteIds = categories.flatMap(x =>
        x.groups.flatMap(g => g.siteIds),
      );
      const disabledSiteIdSet = new Set(
        categories.flatMap(x => x.groups.flatMap(g => g.disabledSiteIds ?? [])),
      );
      const selectedSites = uniq(
        selectedSiteIds.filter(x => !disabledSiteIdSet.has(x)),
      )
        .map(x => getSiteById(x))
        .filter(isNotNull);
      for (const site of selectedSites) {
        taskManager.preInspectionManager.beginSiteInspection({
          flowId: dashboardInspectionFlow.id,
          site,
          inspectionType: SiteInspectionType.Dashboard,
          technicianId: user!.user.memberId,
          technicianName: user!.user.name,
          bundle: 'dashboard',
        });
      }
      setSites(selectedSites);
    }
  }, [
    dashboardInspectionFlow,
    dashboardTemplate,
    getSiteById,
    taskManager.inspectionManager,
    taskManager.preInspectionManager,
    taskNo,
    taskService,
    user,
  ]);

  const onSiteCheckChange = (siteId: number, isDefective: boolean) => {
    const site = getSiteById(siteId);
    const siteInspectionManager =
      taskManager.preInspectionManager.getSiteInspectionManager(siteId);

    if (!site || !siteInspectionManager) return;

    const checkItem = site.checkItems[0];
    if (!checkItem) {
      console.error('check item does not exist');
      return;
    }

    const normalOption = checkItem.options.find(
      x => x.abnormalLevel === AbnormalLevel.Fine,
    );
    const abnormalOption = checkItem.options.find(
      x => x.abnormalLevel !== AbnormalLevel.Fine,
    );

    const abnormalLevel = isDefective
      ? abnormalOption?.abnormalLevel ?? AbnormalLevel.Defective
      : normalOption?.abnormalLevel ?? AbnormalLevel.Fine;
    const severityLevel = isDefective
      ? abnormalOption?.severityLevel ?? SeverityLevel.Warning
      : normalOption?.severityLevel ?? SeverityLevel.None;
    const resultDataStringValue = isDefective
      ? abnormalOption?.label ?? '已点亮'
      : normalOption?.label ?? '未点亮';

    siteInspectionManager.produceItemAt(0, draft => {
      if (draft.type !== 'item') return;

      draft.abnormalLevel = abnormalLevel;
      draft.severityLevel = severityLevel;
      draft.result = {
        valueType: OptionValueType.String,
        value: resultDataStringValue,
      };
    });
  };

  const endDashboardInspection = useCallback(() => {
    taskManager.preInspectionManager.endSiteInspectionBundle('dashboard');
  }, [taskManager.preInspectionManager]);

  return {
    sites,
    dashboardInspectionFlow,
    onSiteCheckChange,
    endDashboardInspection,
    isWatermarkRequired,
    error,
  };
};
